import { endpoints } from "@/configs/Settings";
import { HTTPResponseStatus } from "@/utils/enum";
import { useAuth } from "@/utils/useAuth";
import { useAxios } from "@/utils/useAxios";
import type { TeleconsultoriaActor } from "../types";
import { teleconsultoriaParticipantType } from "../constants";
import type { WherebyRoom } from "../utils/virtualService";

type PatchableResource = "Appointment" | "Encounter";

async function patchResource(
  resourceType: PatchableResource,
  id: string,
  operations: Array<Record<string, unknown>>,
) {
  if (!operations.length) return;

  const url = `${endpoints.EHRPOSTER}/${resourceType}/${id}`;
  const headers = {
    Accept: "application/fhir+json",
    Authorization: await useAuth.getUserToken(),
    "Content-Type": "application/json-patch+json",
  };

  const response = await useAxios.patch(url, operations, {
    headers,
    timeout: 20000,
  });

  if (response.status !== HTTPResponseStatus.Success) {
    throw new Error(`patch ${resourceType}: ${response.status}`);
  }
}

function participantOps(actors: TeleconsultoriaActor[]) {
  return actors.map((actor) => ({
    op: "add",
    path: "/participant/-",
    value: {
      type: teleconsultoriaParticipantType(),
      actor: {
        reference: actor.reference,
        display: actor.display,
      },
    },
  }));
}

/** Inclui actors no fim do array. Não altera os participants já gravados. */
export async function patchParticipants(
  resourceType: PatchableResource,
  id: string,
  actors: TeleconsultoriaActor[],
) {
  await patchResource(resourceType, id, participantOps(actors));
}

/**
 * Liga o Encounter do teleconsultor ao Encounter de origem (host).
 * Feito depois do POST: só então o backend gerou o consentimento do teleconsultor.
 */
export async function patchEncounterPartOf(
  encounterId: string,
  hostEncounterId: string,
) {
  await patchResource("Encounter", encounterId, [
    {
      op: "add",
      path: "/partOf",
      value: { reference: `Encounter/${hostEncounterId}` },
    },
  ]);
}

/**
 * Grava a sala só quando o Appointment ainda não tem virtualService.
 * Array FHIR, lido depois em virtualService[0].
 */
export async function patchAppointmentVirtualService(
  appointmentId: string,
  room: WherebyRoom,
) {
  await patchResource("Appointment", appointmentId, [
    {
      op: "add",
      path: "/virtualService",
      value: [
        {
          addressUrl: room.addressUrl,
          sessionKey: room.sessionKey,
        },
      ],
    },
  ]);
}
